import "./maintenance.scss";
import { ReactNode, useEffect, useRef } from "react";
import { useCommonContext } from "hooks‬/CommonContext";
import { MaintenanceBanner, fmtTime } from "./MaintenanceBanner";
import { MaintenanceFullPage } from "./MaintenanceFullPage";
import { MaintenancePreviewRibbon } from "./MaintenancePreviewRibbon";
import { PRODUCT, SAVING_PAUSED_EVENT, useMaintenance } from "./useMaintenance";

/**
 * Shows the maintenance strip, full page or preview ribbon. The strip slot sits before the
 * children, so a strip appearing never remounts the app.
 */
export function MaintenanceGate({ children }: { children: ReactNode }) {
  const { shown, productNames, client } = useMaintenance();
  const { showErrorToast } = useCommonContext();
  const stripRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onPaused(e: Event) {
      const detail = (e as CustomEvent<{ untilMs: number | null } | undefined>).detail;
      const untilMs = detail ? detail.untilMs : null;
      const after = typeof untilMs === "number" ? `after ${fmtTime(untilMs)}` : "shortly";
      showErrorToast(`Saving is paused for maintenance. Try again ${after}.`);
    }
    window.addEventListener(SAVING_PAUSED_EVENT, onPaused);
    return () => window.removeEventListener(SAVING_PAUSED_EVENT, onPaused);
  }, [showErrorToast]);

  const top = shown[0];
  const outage = top !== undefined && top.phase === "live" && top.window.impact === "FULL_OUTAGE";
  const preview = outage && client.isPreview();
  const hasStrip = top !== undefined && (!outage || preview);

  useEffect(() => {
    const el = stripRef.current;
    const root = document.documentElement;
    if (!hasStrip || !el) return;
    const sync = () => root.style.setProperty("--maintenance-h", `${el.offsetHeight}px`);
    sync();
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(sync);
    if (ro) ro.observe(el);
    return () => {
      if (ro) ro.disconnect();
      root.style.removeProperty("--maintenance-h");
    };
  }, [hasStrip]);

  if (outage && !preview) {
    return (
      <MaintenanceFullPage
        window={top.window}
        productName={productNames[PRODUCT] || "Account portal"}
        onCheck={() => client.requestCheck()}
      />
    );
  }

  return (
    <>
      {hasStrip ? (
        <div ref={stripRef} className="mw-slot">
          {preview ? <MaintenancePreviewRibbon /> : <MaintenanceBanner shown={shown} onDismiss={client.dismiss} />}
        </div>
      ) : null}
      {children}
    </>
  );
}
